import { Select, TextField, Tooltip } from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import CustomModal from "components/lib/customModal";
import { useDemo } from "contexts/DemoContext";
import CONSTANTS from "core/constants";
import { PropsWithChildren, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageAttachment } from "./messageAttachment";
import { Counter, GridContainer } from "./messageModal.styles";
import { SendMessageModalProps } from "./messageModal.types";
import { UnsavedChangesModal } from "./UnsavedChanges";
import { styled } from "styled-components";

export const SendMessageModal = ({
  setModalIsOpen,
  patientName,
  patientUserId,
  open,
  postMessageToPatient,
  selectedTemplateType,
  setSelectedTemplateType,
  messageTemplates,
  isMutating,
}: PropsWithChildren<SendMessageModalProps>) => {
  const { t } = useTranslation();
  const [characterCount, setCharacterCount] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const isSendingRef = useRef(false);

  const attachmentsEnabled = useFlag("MESSAGE_ATTACHMENTS");

  const [uploadedAttachmentKeys, setUploadedAttachmentKeys] = useState<
    string[] | null
  >(null);

  const handleAttachmentUpload = useCallback((files: string[]) => {
    setUploadedAttachmentKeys(files);
  }, []);

  const checkForUnsavedChanges = async () => {
    if (
      attachmentsEnabled &&
      Array.isArray(uploadedAttachmentKeys) &&
      uploadedAttachmentKeys.length > 0
    ) {
      setHasUnsavedChanges(true);
      return;
    }
    setModalIsOpen(false);
  };

  const handleUnsaveCleanup = useCallback(() => {
    setModalIsOpen(false);
    setUploadedAttachmentKeys(null);
  }, [setModalIsOpen]);

  const isSensitiveDataHidden = useDemo();
  const isFreeTextTemplate =
    selectedTemplateType?.templateType === "FREE_TEXT_MESSAGE";
  const isButtonDisabled =
    selectedTemplateType === null ||
    (isFreeTextTemplate && characterCount === 0);

  const [overrideMessageBody, setOverrideMessageBody] = useState("");

  const textFieldValue = () => {
    if (isFreeTextTemplate) return overrideMessageBody;
    return `${
      isSensitiveDataHidden
        ? selectedTemplateType?.body.replace(patientName, CONSTANTS.DEMO)
        : (selectedTemplateType?.body ?? "")
    }`;
  };

  const handleOnSend = useCallback(async () => {
    if (isSendingRef.current) return;
    isSendingRef.current = true;
    await postMessageToPatient({
      overrideMessageBody: isFreeTextTemplate ? overrideMessageBody : undefined,
      ...(uploadedAttachmentKeys
        ? { attachmentKeys: uploadedAttachmentKeys }
        : {}),
    });
    isSendingRef.current = false;
  }, [
    isFreeTextTemplate,
    overrideMessageBody,
    postMessageToPatient,
    uploadedAttachmentKeys,
  ]);

  return (
    <CustomModal.Wrapper
      onClose={() => {
        isSendingRef.current = false;
        checkForUnsavedChanges();
      }}
      titleText={t("PSYCHOLOGIST_MESSAGE_MODAL_TITLE", {
        patient: patientName,
      })}
      open={open}
      size="large"
      isScrollable={true}
      sensitiveData
    >
      <GridContainer>
        <Select value={selectedTemplateType?.value?.toString()}>
          <StyledTooltip content={selectedTemplateType?.label ?? ""}>
            <Select.Trigger
              placeholder={t("SELECT_MESSAGE_TEMPLATE")}
              onClearSelection={() => setSelectedTemplateType(null)}
            >
              {selectedTemplateType?.label}
            </Select.Trigger>
          </StyledTooltip>
          <Select.Options>
            {messageTemplates.map((template) => (
              <Select.Option
                key={template.value}
                onClick={() => setSelectedTemplateType(template)}
              >
                {template.label}
              </Select.Option>
            ))}
          </Select.Options>
        </Select>
        <TextField
          disabled
          label={t("PATIENT_MESSAGE_MODAL_TITLE")}
          value={
            selectedTemplateType?.title ||
            t("PSYCHOLOGIST_MESSAGE_MODAL_PLACEHOLDER")
          }
          onChange={() => null}
          sx={{ width: "100%" }}
          hideSensitiveData={isSensitiveDataHidden}
          testId="sendMessageModalTitle"
          width="full"
        />
        <div>
          <TextField
            maxlength={isFreeTextTemplate ? 1000 : undefined}
            placeholder={isFreeTextTemplate ? selectedTemplateType.body : ""}
            disabled={!isFreeTextTemplate}
            label={t("PATIENT_MESSAGE_MODAL_TITLE_MESSAGE")}
            width="full"
            value={textFieldValue()}
            multiline
            onChange={
              isFreeTextTemplate
                ? (v) => {
                    const { value } = v.target;
                    setCharacterCount(value.length);
                    return setOverrideMessageBody(value);
                  }
                : () => null
            }
            sx={{ width: "100%" }}
            hideSensitiveData={isSensitiveDataHidden}
            testId="sendMessageModalBody"
          />
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {isFreeTextTemplate && <Counter>{characterCount}/1000</Counter>}
        </div>
        {attachmentsEnabled && isFreeTextTemplate && (
          <MessageAttachment
            patientUserId={patientUserId}
            onAttachmentUpload={handleAttachmentUpload}
          />
        )}
        <CustomModal.ButtonRow>
          <CustomModal.Button
            variant="secondary"
            onClick={() => {
              setSelectedTemplateType(null);
              setModalIsOpen(false);
              isSendingRef.current = false;
            }}
            text={t("CANCEL")}
          />
          <CustomModal.Button
            variant="primary"
            disabled={isButtonDisabled}
            onClick={handleOnSend}
            data-testid="cy-send-message-button"
            text={t("SEND_MESSAGE_MODAL")}
            spinning={isMutating}
          ></CustomModal.Button>
        </CustomModal.ButtonRow>
      </GridContainer>
      <UnsavedChangesModal
        open={hasUnsavedChanges}
        onClose={() => setHasUnsavedChanges(false)}
        onSuccess={handleUnsaveCleanup}
        uploadedAttachmentKeys={uploadedAttachmentKeys}
      />
    </CustomModal.Wrapper>
  );
};

// This min-width: 0 is VERY important for text truncation to work inside flex container.
const StyledTooltip = styled(Tooltip)`
  min-width: 0;
`;
